import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Search, Star, Filter } from 'lucide-react';
import WisdomMathFigure from './WisdomMathFigure';

// Sacred Wisdom Library Component
// Embodying the completion and wisdom of R=9
interface WisdomEntry {
  id: string;
  title: string;
  content: string;
  category: string;
  spiritualLevel: number;
  tags: string[];
  createdAt: string;
  source?: string;
  author?: string;
  references?: string[];
}

interface EntryVisualTheme {
  accent: string;
  accentSoft: string;
  textAccent: string;
  cardGradient: string;
  cardGlow: string;
  panelGradient: string;
  chipBg: string;
  chipBorder: string;
  tagBg: string;
  tagBorder: string;
  radius: string;
}

const CATEGORY_THEMES: Record<string, EntryVisualTheme> = {
  numerology: {
    accent: '#f59e0b',
    accentSoft: '#fcd34d',
    textAccent: '#fbbf24',
    cardGradient: 'linear-gradient(150deg, rgba(245, 158, 11, 0.22) 0%, rgba(20, 24, 39, 0.9) 70%)',
    cardGlow: 'rgba(245, 158, 11, 0.24)',
    panelGradient: 'linear-gradient(145deg, rgba(120, 53, 15, 0.45) 0%, rgba(17, 24, 39, 0.72) 100%)',
    chipBg: 'rgba(245, 158, 11, 0.18)',
    chipBorder: 'rgba(245, 158, 11, 0.46)',
    tagBg: 'rgba(251, 191, 36, 0.12)',
    tagBorder: 'rgba(251, 191, 36, 0.32)',
    radius: '22px 12px 22px 12px'
  },
  'sacred-geometry': {
    accent: '#38bdf8',
    accentSoft: '#67e8f9',
    textAccent: '#67e8f9',
    cardGradient: 'linear-gradient(145deg, rgba(14, 165, 233, 0.2) 0%, rgba(8, 47, 73, 0.45) 50%, rgba(15, 23, 42, 0.86) 100%)',
    cardGlow: 'rgba(56, 189, 248, 0.22)',
    panelGradient: 'linear-gradient(145deg, rgba(8, 47, 73, 0.6) 0%, rgba(2, 6, 23, 0.78) 100%)',
    chipBg: 'rgba(56, 189, 248, 0.2)',
    chipBorder: 'rgba(56, 189, 248, 0.44)',
    tagBg: 'rgba(103, 232, 249, 0.12)',
    tagBorder: 'rgba(103, 232, 249, 0.3)',
    radius: '12px 24px 12px 24px'
  },
  meditation: {
    accent: '#34d399',
    accentSoft: '#6ee7b7',
    textAccent: '#6ee7b7',
    cardGradient: 'linear-gradient(145deg, rgba(16, 185, 129, 0.2) 0%, rgba(6, 78, 59, 0.42) 50%, rgba(15, 23, 42, 0.88) 100%)',
    cardGlow: 'rgba(52, 211, 153, 0.24)',
    panelGradient: 'linear-gradient(145deg, rgba(6, 78, 59, 0.62) 0%, rgba(2, 6, 23, 0.8) 100%)',
    chipBg: 'rgba(52, 211, 153, 0.2)',
    chipBorder: 'rgba(52, 211, 153, 0.44)',
    tagBg: 'rgba(110, 231, 183, 0.12)',
    tagBorder: 'rgba(110, 231, 183, 0.3)',
    radius: '24px 14px 24px 14px'
  },
  'sound-healing': {
    accent: '#c084fc',
    accentSoft: '#d8b4fe',
    textAccent: '#e9d5ff',
    cardGradient: 'linear-gradient(145deg, rgba(192, 132, 252, 0.2) 0%, rgba(76, 29, 149, 0.45) 50%, rgba(15, 23, 42, 0.88) 100%)',
    cardGlow: 'rgba(192, 132, 252, 0.24)',
    panelGradient: 'linear-gradient(145deg, rgba(76, 29, 149, 0.62) 0%, rgba(15, 23, 42, 0.8) 100%)',
    chipBg: 'rgba(192, 132, 252, 0.2)',
    chipBorder: 'rgba(192, 132, 252, 0.44)',
    tagBg: 'rgba(216, 180, 254, 0.12)',
    tagBorder: 'rgba(216, 180, 254, 0.3)',
    radius: '18px 26px 18px 26px'
  },
  'energy-work': {
    accent: '#f97316',
    accentSoft: '#fdba74',
    textAccent: '#fdba74',
    cardGradient: 'linear-gradient(145deg, rgba(249, 115, 22, 0.2) 0%, rgba(124, 45, 18, 0.45) 50%, rgba(15, 23, 42, 0.88) 100%)',
    cardGlow: 'rgba(249, 115, 22, 0.24)',
    panelGradient: 'linear-gradient(145deg, rgba(124, 45, 18, 0.62) 0%, rgba(17, 24, 39, 0.8) 100%)',
    chipBg: 'rgba(249, 115, 22, 0.2)',
    chipBorder: 'rgba(249, 115, 22, 0.44)',
    tagBg: 'rgba(253, 186, 116, 0.12)',
    tagBorder: 'rgba(253, 186, 116, 0.3)',
    radius: '26px 12px 26px 12px'
  },
  philosophy: {
    accent: '#a78bfa',
    accentSoft: '#c4b5fd',
    textAccent: '#ddd6fe',
    cardGradient: 'linear-gradient(145deg, rgba(167, 139, 250, 0.2) 0%, rgba(67, 56, 202, 0.42) 50%, rgba(15, 23, 42, 0.88) 100%)',
    cardGlow: 'rgba(167, 139, 250, 0.24)',
    panelGradient: 'linear-gradient(145deg, rgba(67, 56, 202, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)',
    chipBg: 'rgba(167, 139, 250, 0.2)',
    chipBorder: 'rgba(167, 139, 250, 0.44)',
    tagBg: 'rgba(196, 181, 253, 0.12)',
    tagBorder: 'rgba(196, 181, 253, 0.3)',
    radius: '14px 26px 14px 26px'
  },
  collaboration: {
    accent: '#fb7185',
    accentSoft: '#fda4af',
    textAccent: '#fecdd3',
    cardGradient: 'linear-gradient(145deg, rgba(251, 113, 133, 0.2) 0%, rgba(136, 19, 55, 0.42) 50%, rgba(15, 23, 42, 0.88) 100%)',
    cardGlow: 'rgba(251, 113, 133, 0.24)',
    panelGradient: 'linear-gradient(145deg, rgba(136, 19, 55, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)',
    chipBg: 'rgba(251, 113, 133, 0.2)',
    chipBorder: 'rgba(251, 113, 133, 0.44)',
    tagBg: 'rgba(253, 164, 175, 0.12)',
    tagBorder: 'rgba(253, 164, 175, 0.3)',
    radius: '24px 18px 24px 18px'
  }
};

const DEFAULT_THEME = CATEGORY_THEMES.philosophy;

const MEANING_MOTIFS: Array<{ matcher: RegExp; apply: Partial<EntryVisualTheme> }> = [
  {
    matcher: /(void|sunyata|emptiness|shunyata)/i,
    apply: {
      accent: '#22d3ee',
      accentSoft: '#67e8f9',
      textAccent: '#a5f3fc',
      cardGradient: 'linear-gradient(145deg, rgba(8, 145, 178, 0.26) 0%, rgba(15, 23, 42, 0.94) 72%)',
      cardGlow: 'rgba(34, 211, 238, 0.22)',
      panelGradient: 'linear-gradient(145deg, rgba(12, 74, 110, 0.68) 0%, rgba(2, 6, 23, 0.86) 100%)',
      radius: '28px 10px 28px 10px'
    }
  },
  {
    matcher: /(golden|phi|fibonacci|ratio)/i,
    apply: {
      accent: '#fbbf24',
      accentSoft: '#fde68a',
      textAccent: '#fde68a',
      cardGradient: 'linear-gradient(145deg, rgba(251, 191, 36, 0.28) 0%, rgba(120, 53, 15, 0.4) 48%, rgba(15, 23, 42, 0.9) 100%)',
      cardGlow: 'rgba(251, 191, 36, 0.24)',
      panelGradient: 'linear-gradient(145deg, rgba(146, 64, 14, 0.64) 0%, rgba(17, 24, 39, 0.82) 100%)',
      radius: '10px 28px 10px 28px'
    }
  },
  {
    matcher: /(heart|love|compassion|coherent)/i,
    apply: {
      accent: '#fb7185',
      accentSoft: '#fda4af',
      textAccent: '#fecdd3',
      cardGradient: 'linear-gradient(145deg, rgba(251, 113, 133, 0.26) 0%, rgba(190, 24, 93, 0.35) 48%, rgba(15, 23, 42, 0.9) 100%)',
      cardGlow: 'rgba(251, 113, 133, 0.24)',
      panelGradient: 'linear-gradient(145deg, rgba(157, 23, 77, 0.62) 0%, rgba(17, 24, 39, 0.82) 100%)'
    }
  },
  {
    matcher: /(breath|pranayama|vipassana|mindful)/i,
    apply: {
      accent: '#2dd4bf',
      accentSoft: '#99f6e4',
      textAccent: '#ccfbf1',
      cardGradient: 'linear-gradient(145deg, rgba(45, 212, 191, 0.24) 0%, rgba(15, 118, 110, 0.38) 50%, rgba(15, 23, 42, 0.9) 100%)',
      cardGlow: 'rgba(45, 212, 191, 0.23)',
      panelGradient: 'linear-gradient(145deg, rgba(15, 118, 110, 0.62) 0%, rgba(2, 6, 23, 0.82) 100%)'
    }
  },
  {
    matcher: /(sound|frequency|harmonic|cymatics|schumann|music)/i,
    apply: {
      accent: '#c084fc',
      accentSoft: '#ddd6fe',
      textAccent: '#ede9fe',
      cardGradient: 'linear-gradient(145deg, rgba(192, 132, 252, 0.24) 0%, rgba(107, 33, 168, 0.4) 48%, rgba(15, 23, 42, 0.9) 100%)',
      cardGlow: 'rgba(192, 132, 252, 0.24)',
      panelGradient: 'linear-gradient(145deg, rgba(107, 33, 168, 0.64) 0%, rgba(15, 23, 42, 0.82) 100%)'
    }
  }
];

const buildEntryTheme = (entry: WisdomEntry): EntryVisualTheme => {
  const base = CATEGORY_THEMES[entry.category] || DEFAULT_THEME;
  const meaningText = `${entry.title} ${entry.tags.join(' ')} ${entry.content}`;
  const motif = MEANING_MOTIFS.find(m => m.matcher.test(meaningText));
  const merged = motif ? { ...base, ...motif.apply } : base;
  const shapeIndex = entry.spiritualLevel % 3;
  const radiusByLevel = [
    merged.radius,
    '16px 26px 16px 26px',
    '28px 14px 28px 14px'
  ];
  return {
    ...merged,
    radius: radiusByLevel[shapeIndex]
  };
};

// Sacred Wisdom Library - Real spiritual knowledge and mathematics
const sampleWisdom: WisdomEntry[] = [
    {
      id: '1',
      title: 'The Sacred Meaning of Fr00t',
      content: 'Fr00t represents the fruit of infinite potential. F(6) brings harmony and nurturing care. R(9) brings completion and universal wisdom. The two zeros (0+0) represent infinite potential and the void from which all creation emerges. T(2) brings partnership, cooperation, and balance. Together: F(6) + R(9) + 0(0) + 0(0) + T(2) = 17 → 1+7 = 8, the number of power, abundance, and cosmic authority.',
      category: 'numerology',
      spiritualLevel: 8,
      tags: ['numerology', 'sacred', 'fr00t', 'pythagorean'],
      createdAt: new Date().toISOString()
    },
    {
      id: '2',
      title: 'The Golden Ratio: φ = 1.618033988749895',
      content: 'The golden ratio (φ, phi) is calculated as (1 + √5)/2. It appears in the Fibonacci sequence, where each ratio Fn+1/Fn approaches φ. Found in nautilus shells, galaxy spirals, flower petals, and ancient architecture from the Parthenon to the Great Pyramid. It represents divine proportion because it creates the most aesthetically pleasing and naturally harmonious forms.',
      category: 'sacred-geometry',
      spiritualLevel: 7,
      tags: ['golden-ratio', 'sacred-geometry', 'fibonacci', 'divine-proportion', 'mathematics'],
      createdAt: new Date().toISOString()
    },
    {
      id: '3',
      title: 'Pythagorean Numerology: All is Number',
      content: 'Pythagoras (c. 570-495 BCE) taught that "All is number." In the Pythagorean system, letters are assigned numbers 1-9 based on their position in the alphabet (A=1, B=2...I=9, J=1, K=2...R=9, S=1...Z=8). Numbers are reduced by adding digits until reaching a single digit or master number (11, 22, 33). Each number carries specific vibration and meaning in the cosmic order.',
      category: 'numerology',
      spiritualLevel: 9,
      tags: ['numerology', 'pythagoras', 'mathematics', 'ancient-wisdom'],
      createdAt: new Date().toISOString()
    },
    {
      id: '5',
      title: 'The Practice of Mindful Presence',
      content: 'Mindfulness is the practice of bringing complete attention to the present moment without judgment. Originated from Buddhist Vipassana meditation (Satipaṭṭhāna), it involves observing breath, body sensations, thoughts, and emotions as they arise. Modern neuroscience shows mindfulness practice increases gray matter in the prefrontal cortex (decision-making) and hippocampus (memory), while decreasing activity in the amygdala (stress response). Regular practice cultivates equanimity, reduces reactivity, and develops the capacity to respond rather than react to life\'s challenges.',
      category: 'meditation',
      spiritualLevel: 7,
      tags: ['mindfulness', 'presence', 'awareness', 'vipassana', 'meditation', 'neuroscience'],
      createdAt: new Date().toISOString()
    },
    {
      id: '6',
      title: 'Meditation and the Infinite Void (Śūnyatā)',
      content: 'The void, or Śūnyatā in Buddhism, is not emptiness as absence but emptiness as infinite potential. It is the pregnant void from which all phenomena arise and to which all returns. Through meditation on emptiness, we recognize that all forms are temporary manifestations of this formless source. The void is symbolized by 0 in Fr00t numerology.',
      category: 'meditation',
      spiritualLevel: 9,
      tags: ['meditation', 'void', 'buddhism', 'emptiness', 'sunyata'],
      createdAt: new Date().toISOString()
    },
    {
      id: '7',
      title: 'The Flower of Life: Blueprint of Creation',
      content: 'The Flower of Life is a geometric pattern of 19 overlapping circles forming a flower-like pattern. It contains the Seed of Life (7 circles), Egg of Life (8 circles), and Fruit of Life (13 circles). When you connect the centers of the Fruit of Life circles, you create Metatron\'s Cube, which contains all five Platonic solids. Found in ancient temples worldwide, including the Temple of Osiris in Egypt.',
      category: 'sacred-geometry',
      spiritualLevel: 8,
      tags: ['flower-of-life', 'sacred-geometry', 'platonic-solids', 'ancient-knowledge'],
      createdAt: new Date().toISOString()
    },
    {
      id: '8',
      title: 'The 432 Hz Natural Tuning',
      content: 'A432 Hz, known as "Verdi\'s A" or "Philosophical Pitch," is mathematically consistent with the patterns of nature. Unlike A440 Hz (modern standard), A432 Hz resonates with water molecules, the Schumann Resonance (Earth\'s heartbeat at 7.83 Hz), and the golden ratio. Ancient instruments and Tibetan singing bowls were tuned to this frequency. It creates more relaxed, harmonious listening experience.',
      category: 'sound-healing',
      spiritualLevel: 7,
      tags: ['432hz', 'natural-tuning', 'harmony', 'music', 'frequencies'],
      createdAt: new Date().toISOString()
    },
    {
      id: '9',
      title: 'Vipassana: The Path of Insight',
      content: 'Vipassana, meaning "to see things as they really are," is one of India\'s most ancient meditation techniques. By observing body sensations with equanimity, we develop insight into the three characteristics of existence: impermanence (anicca), suffering (dukkha), and non-self (anatta). This direct experiential understanding leads to liberation from suffering and the cycle of rebirth.',
      category: 'meditation',
      spiritualLevel: 9,
      tags: ['vipassana', 'buddhism', 'insight', 'meditation', 'liberation'],
      createdAt: new Date().toISOString()
    },
    {
      id: '10',
      title: 'Sacred Partnership: The Power of Two (T=2)',
      content: 'In numerology, 2 represents partnership, balance, and cooperation. The T in Fr00t embodies this principle. In sacred geometry, two circles create the Vesica Piscis, the birth portal. In spirituality, duality leads to unity: masculine/feminine, active/receptive, yin/yang. True partnership occurs when two complete individuals unite, creating something greater than themselves while maintaining their wholeness.',
      category: 'collaboration',
      spiritualLevel: 8,
      tags: ['partnership', 'collaboration', 'duality', 'unity', 'vesica-piscis'],
      createdAt: new Date().toISOString()
    },
    {
      id: '11',
      title: 'Master Number 11: The Intuitive Illuminator',
      content: 'Master number 11 is the first master number, representing intuition, spiritual insight, and enlightenment. It is not reduced to 2 because it carries higher vibration. Those with 11 in their numerology chart often are highly intuitive, sensitive to energy, and serve as lightworkers or spiritual teachers. The number 11 symbolizes the gateway between physical and spiritual realms.',
      category: 'numerology',
      spiritualLevel: 11,
      tags: ['master-numbers', 'intuition', 'enlightenment', 'spirituality'],
      createdAt: new Date().toISOString()
    },
    // REMOVED: Solfeggio "healing frequencies" - unsubstantiated health claims
    // TODO: If including historical Solfeggio, proper musicological research required
    {
      id: '13',
      title: 'Metatron\'s Cube: The Five Platonic Solids',
      content: 'Metatron\'s Cube contains all five Platonic solids: Tetrahedron (fire, 4 faces), Cube (earth, 6 faces), Octahedron (air, 8 faces), Dodecahedron (ether, 12 faces), and Icosahedron (water, 20 faces). These are the only five perfectly symmetrical 3D shapes possible. Plato associated each with an element, teaching that they are the building blocks of reality. The 13 circles connected by 78 lines create this master pattern.',
      category: 'sacred-geometry',
      spiritualLevel: 9,
      tags: ['metatrons-cube', 'platonic-solids', 'sacred-geometry', 'elements', 'plato'],
      createdAt: new Date().toISOString()
    },
    {
      id: '14',
      title: 'The Fibonacci Sequence in Nature',
      content: 'The Fibonacci sequence (1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144...) where each number equals the sum of the previous two, appears throughout nature: spiral galaxies, nautilus shells, flower petals (roses have 5, lilies 3, daisies 34 or 89), pinecones, pineapples, and even in human proportions. The ratio between consecutive Fibonacci numbers approaches the golden ratio (φ = 1.618...), revealing nature\'s inherent mathematical harmony.',
      category: 'sacred-geometry',
      spiritualLevel: 7,
      tags: ['fibonacci', 'nature', 'mathematics', 'golden-ratio', 'spiral'],
      createdAt: new Date().toISOString()
    },
    {
      id: '15',
      title: 'The Schumann Resonance: Earth\'s Heartbeat',
      content: 'The Schumann Resonance is Earth\'s electromagnetic frequency, resonating at approximately 7.83 Hz. Named after physicist Winfried Otto Schumann who predicted it in 1952, it\'s created by lightning discharges in the cavity between Earth\'s surface and ionosphere. This frequency correlates with human alpha brain waves (7-13 Hz), theta states of meditation, and may influence consciousness. Spending time in nature helps synchronize our biorhythms with Earth\'s natural frequency.',
      category: 'energy-work',
      spiritualLevel: 7,
      tags: ['schumann-resonance', 'earth', 'frequency', 'nature', 'consciousness'],
      createdAt: new Date().toISOString()
    },
    {
      id: '16',
      title: 'The Eight-Fold Path to Liberation',
      content: 'The Buddha taught the Noble Eightfold Path as the way to end suffering: Right View (understanding Four Noble Truths), Right Intention (renunciation, goodwill, harmlessness), Right Speech (truthful, harmonious), Right Action (ethical conduct), Right Livelihood (work that helps, not harms), Right Effort (cultivating wholesome states), Right Mindfulness (present awareness), Right Concentration (meditation mastery). The number 8 represents power and completion in numerology.',
      category: 'meditation',
      spiritualLevel: 9,
      tags: ['buddhism', 'eight-fold-path', 'dharma', 'liberation', 'wisdom'],
      createdAt: new Date().toISOString()
    },
    {
      id: '17',
      title: 'The Torus: Universal Energy Flow Pattern',
      content: 'The torus is the fundamental energy flow pattern of the universe, appearing in everything from atoms to galaxies. Energy flows in through the top, spirals down and out through the sides, and returns to flow in again—creating a self-sustaining field. The human heart generates a toroidal electromagnetic field extending 8-12 feet. This pattern (donut shape) represents wholeness, infinite recycling, and sustainable energy flow. Mathematical formula: x=(R+r*cos(v))*cos(u), y=(R+r*cos(v))*sin(u), z=r*sin(v).',
      category: 'sacred-geometry',
      spiritualLevel: 8,
      tags: ['torus', 'energy-flow', 'vortex', 'sacred-geometry', 'heart-field'],
      createdAt: new Date().toISOString()
    },
    {
      id: '18',
      title: 'Cymatics: Seeing Sound, Hearing Geometry',
      content: 'Cymatics is the study of visible sound vibration. When you vibrate a plate covered in sand or water at specific frequencies, geometric patterns emerge. At 528 Hz, water forms perfect hexagonal patterns. Different frequencies create different sacred geometric patterns: circles, hexagons, stars, mandalas. This proves that sound and geometry are intimately connected—vibration creates form. As Pythagoras taught, "Geometry is frozen music."',
      category: 'sound-healing',
      spiritualLevel: 8,
      tags: ['cymatics', 'sound', 'geometry', 'vibration', 'water'],
      createdAt: new Date().toISOString()
    },
    {
      id: '19',
      title: 'The Sri Yantra: Geometry of Manifestation',
      content: 'The Sri Yantra consists of nine interlocking triangles: four pointing upward (Shiva, masculine principle) and five pointing downward (Shakti, feminine principle), creating 43 triangular regions. At the center is the bindu, the point of creation. The pattern is surrounded by two rings of lotus petals (8 and 16) and a square (bhupura). This sacred diagram represents the union of divine masculine and feminine energies, the creative process, and the entire cosmos in geometric form.',
      category: 'sacred-geometry',
      spiritualLevel: 9,
      tags: ['sri-yantra', 'yantra', 'tantra', 'manifestation', 'sacred-geometry'],
      createdAt: new Date().toISOString()
    },
    {
      id: '20',
      title: 'Coherent Heart Rhythm: The Intelligence of the Heart',
      content: 'Research by the HeartMath Institute shows that the heart generates the strongest electromagnetic field in the body (5000 times stronger than the brain). When we cultivate positive emotions like love, gratitude, and compassion, our heart rhythm becomes coherent—a smooth, sine wave-like pattern. This coherence synchronizes brain waves, improves cognition, and radiates to affect others within our field. The heart truly has intelligence independent of the brain.',
      category: 'energy-work',
      spiritualLevel: 7,
      tags: ['heart-coherence', 'heartmath', 'love', 'electromagnetic-field', 'science'],
      createdAt: new Date().toISOString()
    },
    {
      id: '21',
      title: 'The Merkaba: Light Body Vehicle',
      content: 'The Merkaba (Mer=Light, Ka=Spirit, Ba=Body) is a star tetrahedron formed by two interlocking tetrahedra spinning in opposite directions. One points upward (masculine), one downward (feminine). When activated through specific breathing and visualization, it creates an interdimensional energy field for spiritual travel and protection. The counter-rotating fields spin at a 34/21 Fibonacci ratio. Ancient Egyptians and many wisdom traditions describe this as the light body or vehicle of ascension.',
      category: 'energy-work',
      spiritualLevel: 9,
      tags: ['merkaba', 'light-body', 'sacred-geometry', 'ascension', 'meditation'],
      createdAt: new Date().toISOString()
    },
    {
      id: '22',
      title: 'As Above, So Below: The Hermetic Principle',
      content: 'This profound Hermetic axiom teaches that the microcosm reflects the macrocosm. The patterns in atoms mirror patterns in solar systems and galaxies. The structure of the human body reflects cosmic organization. What happens in consciousness affects physical reality. This principle, from the Emerald Tablet of Hermes Trismegistus, reveals the interconnected holographic nature of existence. Understanding one level provides insight into all levels.',
      category: 'philosophy',
      spiritualLevel: 8,
      tags: ['hermeticism', 'emerald-tablet', 'microcosm', 'macrocosm', 'ancient-wisdom'],
      createdAt: new Date().toISOString()
    },
    {
      id: '23',
      title: 'Breath as Bridge Between Body and Spirit',
      content: 'The breath is the only autonomic function we can consciously control, making it the bridge between body and spirit. Pranayama (control of prana/life force) is one of the eight limbs of yoga. The 4-7-8 breathing technique (inhale 4, hold 7, exhale 8) activates the parasympathetic nervous system. Box breathing (4-4-4-4) used by Navy SEALs creates calm focus. Alternate nostril breathing (Nadi Shodhana) balances left/right brain hemispheres.',
      category: 'meditation',
      spiritualLevel: 6,
      tags: ['breath', 'pranayama', 'yoga', 'meditation', 'techniques'],
      createdAt: new Date().toISOString()
    },
    {
      id: '24',
      title: 'The Vesica Piscis: Sacred Portal of Creation',
      content: 'The Vesica Piscis is formed by two circles of equal radius, each passing through the other\'s center. The overlap creates an almond shape (mandorla) with a width-to-height ratio of 1:√3 (1:1.732...). This is one of the most sacred ratios in geometry. The Vesica is the birth canal of geometric creation—from it emerges the triangle, square, pentagon, and eventually all of sacred geometry. In Christianity, it surrounds images of Christ, representing divine birth. The square root of 3 connects it to the trinity principle.',
      category: 'sacred-geometry',
      spiritualLevel: 8,
      tags: ['vesica-piscis', 'sacred-geometry', 'creation', 'portal', 'trinity'],
      createdAt: new Date().toISOString()
    },
    {
      id: '25',
      title: 'The Law of Vibration: Everything is Energy',
      content: 'Quantum physics confirms what ancient mystics knew: everything is vibration. Solid matter is 99.9999% empty space with subatomic particles vibrating at specific frequencies. Your thoughts vibrate, your emotions vibrate, your body vibrates. Like frequencies resonate together (law of attraction). By raising your vibration through meditation, sound healing, and positive emotions, you align with higher frequencies of consciousness, health, and manifestation.',
      category: 'energy-work',
      spiritualLevel: 8,
      tags: ['vibration', 'quantum-physics', 'energy', 'frequency', 'consciousness'],
      createdAt: new Date().toISOString()
    },
    {
      id: '26',
      title: 'The Tao Te Ching: The Way of Nature',
      content: 'The Tao Te Ching, attributed to Lao Tzu (c. 6th century BCE), teaches wu wei (non-action or effortless action)—acting in harmony with the natural flow of life rather than forcing outcomes. The Tao (Way) is the unnameable source of all things, both empty and full, moving in cycles of yin and yang. "The journey of a thousand miles begins with one step" (Chapter 64). The sage acts without striving, teaches without words, and finds contentment in simplicity. This ancient Chinese text has influenced philosophy, martial arts, and meditation practices for over 2,500 years.',
      category: 'philosophy',
      spiritualLevel: 9,
      tags: ['tao-te-ching', 'lao-tzu', 'taoism', 'wu-wei', 'ancient-wisdom', 'chinese-philosophy'],
      source: 'Tao Te Ching (c. 6th century BCE)',
      author: 'Lao Tzu (attributed)',
      references: ['D.C. Lau translation (Penguin Classics)', 'Stephen Mitchell translation', 'Chinese philosophical tradition'],
      createdAt: new Date().toISOString()
    },
    {
      id: '27',
      title: 'The I Ching: Book of Changes',
      content: 'The I Ching (易經), or Book of Changes, is one of the oldest Chinese texts, dating to the Western Zhou period (1000-750 BCE). It consists of 64 hexagrams formed by combining two trigrams (three lines each, broken or solid). Each hexagram represents a situation and offers guidance through change. The core principle: "The only constant is change." By consulting the I Ching through traditional methods (coin tossing or yarrow stalks), one gains insight into the dynamic flow of circumstances. The binary system (yin/yang lines) predates modern binary code by millennia.',
      category: 'philosophy',
      spiritualLevel: 8,
      tags: ['i-ching', 'book-of-changes', 'divination', 'chinese-philosophy', 'hexagrams', 'yin-yang'],
      source: 'I Ching (易經), Western Zhou period',
      author: 'Traditional Chinese wisdom',
      references: ['Richard Wilhelm translation', 'James Legge translation', 'Chinese divination tradition'],
      createdAt: new Date().toISOString()
    },
    {
      id: '28',
      title: 'The Kabbalistic Tree of Life: Ten Sephiroth',
      content: 'The Tree of Life (Etz Chaim) is the central symbol of Kabbalah, Jewish mystical tradition. It consists of 10 Sephiroth (emanations) arranged in three pillars: Right (Mercy/Chesed), Left (Severity/Geburah), and Middle (Balance). The 22 paths connecting them correspond to the 22 Hebrew letters. From Kether (Crown, the source) to Malkuth (Kingdom, physical reality), the Tree maps the descent of divine light into manifestation and the path of return through spiritual ascent. Each Sephirah represents an aspect of consciousness and reality, from divine wisdom (Chokmah) to earthly foundation (Yesod).',
      category: 'sacred-geometry',
      spiritualLevel: 9,
      tags: ['kabbalah', 'tree-of-life', 'sephiroth', 'jewish-mysticism', 'hebrew-letters', 'spiritual-ascent'],
      source: 'Kabbalistic tradition',
      author: 'Jewish mystics (medieval period)',
      references: ['Sefer Yetzirah (Book of Creation)', 'Zohar (Book of Splendor)', 'Dion Fortune: "The Mystical Qabalah"'],
      createdAt: new Date().toISOString()
    },
    {
      id: '29',
      title: 'The Seven Chakras: Energy Centers of the Body',
      content: 'The chakra system originates from ancient Indian texts (Vedas, Upanishads, c. 1500-500 BCE). Seven main energy centers align along the spine: Root (Muladhara, red, survival), Sacral (Svadhisthana, orange, creativity), Solar Plexus (Manipura, yellow, power), Heart (Anahata, green, love), Throat (Vishuddha, blue, communication), Third Eye (Ajna, indigo, intuition), and Crown (Sahasrara, violet, transcendence). Each chakra corresponds to specific organs, emotions, and life themes. Modern research shows chakra locations align with major nerve plexuses and endocrine glands, suggesting a physiological basis for this ancient wisdom.',
      category: 'energy-work',
      spiritualLevel: 8,
      tags: ['chakras', 'energy-centers', 'yoga', 'hinduism', 'tantra', 'energy-healing'],
      source: 'Vedic and Tantric traditions',
      author: 'Ancient Indian sages',
      references: ['Upanishads', 'Tantric texts', 'Anodea Judith: "Wheels of Life"', 'C.W. Leadbeater: "The Chakras"'],
      createdAt: new Date().toISOString()
    },
    {
      id: '30',
      title: 'Quantum Consciousness: The Observer Effect',
      content: 'The double-slit experiment (Thomas Young, 1801; quantum version, 1927) reveals that particles behave as waves until observed, then collapse into particles. This suggests consciousness affects reality at the quantum level. The Copenhagen Interpretation (Bohr, Heisenberg) proposes that observation creates reality. Modern experiments show quantum effects in biological systems (photosynthesis, bird navigation). Some physicists (Penrose, Hameroff) propose consciousness arises from quantum processes in microtubules. This bridges ancient wisdom ("mind creates reality") with cutting-edge physics.',
      category: 'philosophy',
      spiritualLevel: 9,
      tags: ['quantum-physics', 'consciousness', 'observer-effect', 'double-slit', 'quantum-mechanics', 'reality'],
      source: 'Quantum physics research',
      author: 'Various physicists',
      references: ['Niels Bohr: Copenhagen Interpretation', 'Roger Penrose: "The Emperor\'s New Mind"', 'Stuart Hameroff: Orchestrated Objective Reduction theory'],
      createdAt: new Date().toISOString()
    },
    {
      id: '31',
      title: 'Zen Buddhism: Direct Pointing to Mind',
      content: 'Zen (Chan in Chinese, from Sanskrit dhyana/meditation) emphasizes direct experience over scriptures. Founded by Bodhidharma (c. 5th-6th century CE) in China, it spread to Japan. Core practice: zazen (sitting meditation) and koan study (paradoxical questions like "What is the sound of one hand clapping?"). Zen teaches that Buddha-nature is already present; we need only recognize it. "Before enlightenment, chop wood, carry water. After enlightenment, chop wood, carry water." The emphasis on present-moment awareness and non-dual realization has influenced art, poetry, and modern mindfulness practices.',
      category: 'meditation',
      spiritualLevel: 9,
      tags: ['zen', 'buddhism', 'chan', 'koan', 'zazen', 'enlightenment', 'non-duality'],
      source: 'Buddhist tradition',
      author: 'Bodhidharma, various Zen masters',
      references: ['D.T. Suzuki: "Zen Mind, Beginner\'s Mind"', 'Shunryu Suzuki: "Zen Mind, Beginner\'s Mind"', 'The Platform Sutra of the Sixth Patriarch'],
      createdAt: new Date().toISOString()
    },
    {
      id: '32',
      title: 'Stoicism: The Art of Living',
      content: 'Stoicism, founded by Zeno of Citium (c. 300 BCE), teaches that virtue is the only good and that we should focus on what we can control. Key principles: distinguish between what is and isn\'t in our control, accept what cannot be changed, and act with wisdom, courage, justice, and temperance. Marcus Aurelius\' "Meditations," Epictetus\' "Enchiridion," and Seneca\'s letters provide practical guidance. The dichotomy of control: we control our judgments, desires, and actions—not external events. This philosophy has influenced cognitive-behavioral therapy and modern resilience practices.',
      category: 'philosophy',
      spiritualLevel: 7,
      tags: ['stoicism', 'marcus-aurelius', 'epictetus', 'seneca', 'virtue', 'resilience', 'ancient-philosophy'],
      source: 'Hellenistic philosophy',
      author: 'Zeno of Citium, Marcus Aurelius, Epictetus, Seneca',
      references: ['Marcus Aurelius: "Meditations"', 'Epictetus: "Enchiridion"', 'Seneca: "Letters from a Stoic"'],
      createdAt: new Date().toISOString()
    },
    {
      id: '33',
      title: 'Alchemy: Transformation of the Self',
      content: 'Alchemy, practiced in ancient Egypt, China, India, and medieval Europe, operates on multiple levels: physical (transmuting base metals to gold), psychological (transforming the shadow into light), and spiritual (uniting opposites to achieve wholeness). The alchemical process: nigredo (blackening, dissolution), albedo (whitening, purification), citrinitas (yellowing, illumination), and rubedo (reddening, completion). Carl Jung saw alchemy as a metaphor for individuation—integrating conscious and unconscious. The philosopher\'s stone represents the perfected self. Modern applications: personal transformation, shadow work, and psychological integration.',
      category: 'philosophy',
      spiritualLevel: 8,
      tags: ['alchemy', 'transformation', 'individuation', 'carl-jung', 'shadow-work', 'philosophers-stone'],
      source: 'Alchemical tradition',
      author: 'Various alchemists',
      references: ['Carl Jung: "Psychology and Alchemy"', 'Hermetic tradition', 'Emerald Tablet of Hermes Trismegistus'],
      createdAt: new Date().toISOString()
    },
    {
      id: '34',
      title: 'Sacred Architecture: Temples of Alignment',
      content: 'Ancient sacred sites worldwide align with astronomical events and incorporate sacred geometry. Stonehenge (c. 3000-2000 BCE) aligns with solstices. The Great Pyramid of Giza incorporates π and φ (golden ratio) in its proportions. Chartres Cathedral uses golden ratio and √2 in its design. Angkor Wat aligns with equinoxes. These structures were built using precise mathematical knowledge, suggesting ancient understanding of Earth\'s relationship to cosmos. Modern research shows many sacred sites sit on ley lines (hypothetical energy lines) and geological fault lines, possibly amplifying natural energies.',
      category: 'sacred-geometry',
      spiritualLevel: 8,
      tags: ['sacred-architecture', 'stonehenge', 'great-pyramid', 'ley-lines', 'astronomical-alignment', 'ancient-knowledge'],
      source: 'Archaeological and architectural research',
      author: 'Various ancient cultures',
      references: ['Graham Hancock: "Fingerprints of the Gods"', 'John Michell: "The New View Over Atlantis"', 'Archaeological studies of ancient sites'],
      createdAt: new Date().toISOString()
    },
    {
      id: '35',
      title: 'The Mayan Calendar: Cycles of Time',
      content: 'The Maya developed sophisticated calendar systems tracking multiple cycles simultaneously. The Tzolk\'in (260 days) combines 20 day names with 13 numbers, creating a sacred cycle. The Haab\' (365 days) is the solar calendar. The Long Count tracks longer periods (baktuns of 144,000 days). The 2012 phenomenon marked the end of a 5,125-year cycle. Maya understood time as cyclical, not linear. Their astronomical observations were remarkably accurate—predicting eclipses and planetary alignments. This cyclical view of time appears in many indigenous traditions, contrasting with Western linear time.',
      category: 'philosophy',
      spiritualLevel: 7,
      tags: ['mayan-calendar', 'time-cycles', 'tzolkin', 'long-count', 'indigenous-wisdom', 'astronomy'],
      source: 'Maya civilization',
      author: 'Maya astronomers and priests',
      references: ['Maya codices (Dresden, Madrid, Paris)', 'Archaeological research at Maya sites', 'John Major Jenkins: "Maya Cosmogenesis 2012"'],
      createdAt: new Date().toISOString()
    },
    {
      id: '36',
      title: 'Advaita Vedanta: Non-Dual Reality',
      content: 'Advaita Vedanta, systematized by Adi Shankara (c. 8th century CE), teaches that ultimate reality (Brahman) is non-dual—there is only One without a second. The individual self (Atman) is identical to Brahman. The world (Maya) is an appearance, like a rope mistaken for a snake. Liberation (Moksha) comes through direct realization: "I am That" (Tat Tvam Asi). The three states of consciousness—waking, dreaming, deep sleep—point to a fourth state (Turiya), pure awareness. This non-dual philosophy has influenced modern teachers like Ramana Maharshi and Nisargadatta Maharaj.',
      category: 'philosophy',
      spiritualLevel: 10,
      tags: ['advaita-vedanta', 'non-duality', 'brahman', 'atman', 'moksha', 'hindu-philosophy', 'enlightenment'],
      source: 'Vedantic tradition',
      author: 'Adi Shankara, Upanishadic sages',
      references: ['Upanishads (especially Mandukya)', 'Adi Shankara: "Vivekachudamani"', 'Ramana Maharshi: "Who Am I?"'],
      createdAt: new Date().toISOString()
    },
    {
      id: '37',
      title: 'Lucid Dreaming: Consciousness in Sleep',
      content: 'Lucid dreaming is the practice of becoming aware that one is dreaming while still in the dream state. Ancient Tibetan dream yoga (Milam) and practices in various traditions have cultivated this skill. Modern research (LaBerge, 1980s) confirmed lucid dreaming scientifically using eye-movement signals. Benefits include: overcoming nightmares, creative problem-solving, practicing skills, and exploring consciousness. Techniques: reality checks (questioning if you\'re dreaming), dream journaling, and MILD (Mnemonic Induction of Lucid Dreams). The state between waking and dreaming (hypnagogia) is also a gateway to altered states.',
      category: 'meditation',
      spiritualLevel: 7,
      tags: ['lucid-dreaming', 'dream-yoga', 'consciousness', 'sleep', 'tibetan-buddhism', 'altered-states'],
      source: 'Various traditions, modern research',
      author: 'Tibetan masters, Stephen LaBerge',
      references: ['Stephen LaBerge: "Exploring the World of Lucid Dreaming"', 'Tibetan dream yoga texts', 'Carlos Castaneda: "The Art of Dreaming"'],
      createdAt: new Date().toISOString()
    },
    {
      id: '38',
      title: 'Shadow Work: Integrating the Unconscious',
      content: 'Shadow work, developed by Carl Jung, involves integrating the rejected, hidden aspects of ourselves (the shadow). The shadow contains qualities we deny or project onto others. By acknowledging and integrating the shadow, we become more whole and authentic. Methods: journaling, active imagination, dream analysis, and honest self-reflection. The shadow isn\'t evil—it\'s simply unconscious. When integrated, its energy becomes available for growth. This work is essential for spiritual development, as unacknowledged shadow material can sabotage our highest intentions. "Until you make the unconscious conscious, it will direct your life and you will call it fate" (Jung).',
      category: 'philosophy',
      spiritualLevel: 8,
      tags: ['shadow-work', 'carl-jung', 'individuation', 'unconscious', 'integration', 'psychology', 'self-development'],
      source: 'Jungian psychology',
      author: 'Carl Jung',
      references: ['Carl Jung: "Aion"', 'Robert Johnson: "Owning Your Own Shadow"', 'Debbie Ford: "The Dark Side of the Light Chasers"'],
      createdAt: new Date().toISOString()
    },
    {
      id: '39',
      title: 'Sacred Numbers: Beyond Basic Numerology',
      content: 'Beyond 1-9, certain numbers hold special significance across traditions. 108 appears in Hinduism (108 beads in mala, 108 Upanishads), Buddhism (108 defilements), and appears in astronomy (distance ratios Sun/Earth, Moon/Earth). 144 (12×12) appears in Revelation and represents completion. 432,000 appears in Hindu cosmology (duration of Kali Yuga). 72 appears in multiple traditions (72 names of God in Kabbalah, 72 disciples in some traditions). 360 (degrees in circle) connects to cycles. These numbers often relate to astronomical cycles, suggesting ancient awareness of cosmic rhythms encoded in sacred mathematics.',
      category: 'numerology',
      spiritualLevel: 8,
      tags: ['sacred-numbers', '108', '144', '432000', '72', '360', 'cosmology', 'astronomy'],
      source: 'Multiple traditions',
      author: 'Various ancient cultures',
      references: ['Hindu cosmology texts', 'Kabbalistic numerology', 'Astronomical observations', 'Michael Schneider: "A Beginner\'s Guide to Constructing the Universe"'],
      createdAt: new Date().toISOString()
    },
    {
      id: '40',
      title: 'Music Theory: Harmonics and Overtones',
      content: 'Music theory reveals mathematical relationships in sound. The harmonic series (fundamental frequency and its multiples) creates overtones. A note\'s octave is 2:1 ratio, perfect fifth is 3:2, perfect fourth is 4:3. These ratios appear in nature and sacred geometry. Pythagorean tuning uses pure intervals, while equal temperament (modern standard) slightly adjusts intervals for versatility. The circle of fifths shows relationships between keys. Chords create harmonic resonance. Ancient cultures tuned instruments to natural harmonics. Modern research shows music affects brain waves, heart rate, and emotional states. "Music is the arithmetic of sounds as optics is the geometry of light" (Claude Debussy).',
      category: 'sound-healing',
      spiritualLevel: 7,
      tags: ['music-theory', 'harmonics', 'overtones', 'pythagorean-tuning', 'intervals', 'frequency-ratios', 'resonance'],
      source: 'Music theory and acoustics',
      author: 'Pythagoras, various musicians and theorists',
      references: ['Pythagorean tuning system', 'Just intonation', 'Modern music theory texts', 'Acoustics research'],
      createdAt: new Date().toISOString()
    },
    {
      id: '41',
      title: 'The Bhagavad Gita: The Song of the Lord',
      content: 'The Bhagavad Gita (c. 2nd century BCE-2nd century CE) is a 700-verse dialogue between Arjuna and Krishna on the battlefield of Kurukshetra. It presents three paths to liberation: Karma Yoga (selfless action), Bhakti Yoga (devotion), and Jnana Yoga (knowledge). Krishna teaches that one should act without attachment to results, perform one\'s duty (dharma), and recognize the eternal Self (Atman) beyond the temporary body. "You have the right to work, but never to the fruit of work" (2.47). This text has influenced Gandhi, Thoreau, and countless seekers, offering practical wisdom for living with spiritual awareness.',
      category: 'philosophy',
      spiritualLevel: 9,
      tags: ['bhagavad-gita', 'hinduism', 'karma-yoga', 'bhakti-yoga', 'dharma', 'ancient-wisdom', 'indian-philosophy'],
      source: 'Mahabharata epic',
      author: 'Traditional (attributed to Vyasa)',
      references: ['Bhagavad Gita (various translations)', 'Eknath Easwaran translation', 'Swami Prabhavananda translation'],
      createdAt: new Date().toISOString()
    },
    {
      id: '42',
      title: 'The Four Noble Truths: Foundation of Buddhism',
      content: 'The Buddha\'s first teaching after enlightenment presents the Four Noble Truths: (1) Dukkha—life involves suffering, dissatisfaction, and impermanence; (2) Samudaya—suffering arises from craving, attachment, and ignorance; (3) Nirodha—suffering can cease through eliminating its causes; (4) Magga—the path to cessation is the Noble Eightfold Path. These truths aren\'t pessimistic but realistic, offering a diagnosis and cure. Modern psychology recognizes similar patterns: suffering from attachment, the possibility of change through awareness, and practical methods for transformation. This framework applies to all aspects of life.',
      category: 'meditation',
      spiritualLevel: 9,
      tags: ['four-noble-truths', 'buddhism', 'dukkha', 'suffering', 'eightfold-path', 'dharma', 'enlightenment'],
      source: 'Buddhist tradition',
      author: 'The Buddha (Siddhartha Gautama)',
      references: ['Dhammacakkappavattana Sutta (First Discourse)', 'Pali Canon', 'Various Buddhist commentaries'],
      createdAt: new Date().toISOString()
    },
    {
      id: '43',
      title: 'The Hermetic Principles: Seven Universal Laws',
      content: 'The Kybalion (1908, attributed to "Three Initiates") outlines seven Hermetic principles: (1) Mentalism—"All is Mind," (2) Correspondence—"As above, so below," (3) Vibration—everything vibrates, (4) Polarity—everything has opposites, (5) Rhythm—everything flows in cycles, (6) Cause and Effect—every cause has an effect, (7) Gender—masculine and feminine principles in all things. These principles, derived from the Emerald Tablet and Hermetic tradition, provide a framework for understanding reality and personal transformation. They bridge ancient wisdom and modern understanding of physics, psychology, and consciousness.',
      category: 'philosophy',
      spiritualLevel: 8,
      tags: ['hermetic-principles', 'kybalion', 'seven-laws', 'hermeticism', 'emerald-tablet', 'universal-laws'],
      source: 'Hermetic tradition',
      author: 'Attributed to Hermes Trismegistus',
      references: ['The Kybalion (1908)', 'Emerald Tablet of Hermes Trismegistus', 'Hermetic texts'],
      createdAt: new Date().toISOString()
    },
    {
      id: '44',
      title: 'Indigenous Wisdom: Living in Harmony',
      content: 'Indigenous cultures worldwide share wisdom about living in harmony with nature, community, and spirit. Common themes: all life is interconnected, respect for elders and ancestors, ceremonies marking life transitions, gratitude for Earth\'s gifts, and understanding that we borrow the Earth from future generations. The Lakota phrase "Mitakuye Oyasin" (all my relations) expresses this interconnectedness. Many traditions use vision quests, sweat lodges, and plant medicines (used respectfully and traditionally) for spiritual growth. This wisdom offers crucial perspectives for addressing modern environmental and social challenges.',
      category: 'philosophy',
      spiritualLevel: 8,
      tags: ['indigenous-wisdom', 'native-american', 'harmony', 'nature', 'community', 'ceremony', 'ancestors'],
      source: 'Various indigenous traditions',
      author: 'Indigenous elders and communities',
      references: ['Oral traditions', 'Respectful engagement with indigenous communities', 'Academic studies of indigenous cultures'],
      createdAt: new Date().toISOString()
    },
    {
      id: '45',
      title: 'The Enneagram: Nine Types of Consciousness',
      content: 'The Enneagram is a personality and consciousness system describing nine types, each with a core motivation and fear. Types: 1-Perfectionist, 2-Helper, 3-Achiever, 4-Individualist, 5-Investigator, 6-Loyalist, 7-Enthusiast, 8-Challenger, 9-Peacemaker. Each type has healthy, average, and unhealthy levels. The system includes wings (adjacent types), arrows (stress and security directions), and triads (gut/heart/head centers). Origins trace to Sufi tradition, with modern development by Gurdjieff, Ichazo, and Naranjo. It\'s a tool for self-awareness, understanding others, and spiritual growth, revealing both our gifts and our egoic patterns.',
      category: 'philosophy',
      spiritualLevel: 7,
      tags: ['enneagram', 'personality-types', 'consciousness', 'self-awareness', 'sufism', 'gurdjieff'],
      source: 'Sufi tradition, modern psychology',
      author: 'G.I. Gurdjieff, Oscar Ichazo, Claudio Naranjo',
      references: ['G.I. Gurdjieff: "Beelzebub\'s Tales"', 'Don Richard Riso: "The Wisdom of the Enneagram"', 'Helen Palmer: "The Enneagram"'],
      createdAt: new Date().toISOString()
    },
    {
      id: '46',
      title: 'Platonic Space: The Receptacle (Khôra) and the Medium of Manifestation',
      content: 'In Plato\'s Timaeus (c. 360 BCE), he describes three fundamental components of reality: (1) The intelligible realm of Being (the Forms/Ideas), (2) The sensible realm of Becoming (physical world), and (3) The Receptacle (Khôra/Χώρα)—the formless, neutral space that allows the Forms to manifest in the physical world. The Receptacle is described as "that which receives all things" and is "apprehended without sensation." It is neither Being nor Becoming, but a third kind—the medium through which the eternal Forms take on temporal existence. Plato uses the metaphor of a goldsmith who shapes gold into various forms: the gold (Receptacle) remains constant while the forms change. This concept suggests that space itself is not empty but is a dynamic, receptive medium that "gives space" to all generated things. The Receptacle is essential for understanding how the perfect, unchanging Forms can manifest in the imperfect, changing physical world. Modern interpretations connect this to quantum field theory, where space is not empty but filled with potential (vacuum energy). The five Platonic solids (tetrahedron, cube, octahedron, dodecahedron, icosahedron) are the only perfect geometric forms that can exist in three-dimensional space, representing the Forms manifesting through the Receptacle. This philosophical concept bridges metaphysics, geometry, and physics, offering insight into how ideal forms become physical reality.',
      category: 'philosophy',
      spiritualLevel: 10,
      tags: ['plato', 'timaeus', 'khora', 'receptacle', 'space', 'metaphysics', 'forms', 'platonic-solids', 'ancient-philosophy', 'manifestation'],
      source: 'Plato\'s Timaeus (c. 360 BCE)',
      author: 'Plato (428/427-348/347 BCE)',
      references: [
        'Plato: "Timaeus" (c. 360 BCE), translated by Benjamin Jowett',
        'Plato: "Timaeus" (c. 360 BCE), translated by R.G. Bury (Loeb Classical Library)',
        'John Sallis: "Chorology: On Beginning in Plato\'s Timaeus" (1999)',
        'Jacques Derrida: "Khôra" (1993) - deconstructive analysis of the Receptacle',
        'Francis M. Cornford: "Plato\'s Cosmology: The Timaeus of Plato" (1937)',
        'Gregory Vlastos: "Plato\'s Universe" (1975)',
        'Modern physics connection: Quantum field theory and vacuum energy',
        'Mathematical connection: Platonic solids as perfect forms in space'
      ],
      createdAt: new Date().toISOString()
    }
];

const WisdomLibrary: React.FC = () => {
  const [wisdomEntries, setWisdomEntries] = useState<WisdomEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    setWisdomEntries(sampleWisdom);
  }, []);

  // Filter wisdom entries
  const filteredWisdom = useMemo(() => {
    return wisdomEntries.filter(entry => {
      const matchesSearch = entry.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           entry.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           entry.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategory === 'all' || entry.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [wisdomEntries, searchQuery, selectedCategory]);

  // Get unique categories
  const categories = useMemo(() => {
    return ['all', ...Array.from(new Set(wisdomEntries.map(e => e.category)))];
  }, [wisdomEntries]);

  return (
    <div className="fr00t-wisdom-library">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Search and Filter Bar */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.9)',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '2rem',
          border: '1px solid rgba(99, 102, 241, 0.3)'
        }}>
          {/* Search Input */}
          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <Search size={20} style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#64748b'
            }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search sacred wisdom..."
              style={{
                width: '100%',
                padding: '12px 12px 12px 44px',
                background: 'rgba(15, 23, 42, 0.8)',
                border: '2px solid rgba(99, 102, 241, 0.3)',
                borderRadius: '12px',
                color: '#f1f5f9',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Category Filter */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <Filter size={18} color="#6366f1" />
            {categories.map(category => (
              <motion.button
                key={category}
                onClick={() => setSelectedCategory(category)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  padding: '8px 16px',
                  background: selectedCategory === category ? 
                    'rgba(99, 102, 241, 0.3)' : 'rgba(100, 116, 139, 0.1)',
                  border: selectedCategory === category ?
                    '2px solid #6366f1' : '2px solid rgba(100, 116, 139, 0.2)',
                  borderRadius: '8px',
                  color: selectedCategory === category ? '#6366f1' : '#64748b',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Wisdom Entries Grid */}
        <AnimatePresence mode="wait">
          {filteredWisdom.length > 0 ? (
            <div style={{
              display: 'grid',
              gap: '1.5rem'
            }}>
              {filteredWisdom.map((entry, index) => {
                const theme = buildEntryTheme(entry);
                return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="fr00t-card"
                  whileHover={{ scale: 1.01 }}
                  style={{
                    background: `radial-gradient(circle at 88% 10%, ${theme.cardGlow} 0%, transparent 36%), ${theme.cardGradient}`,
                    border: `1px solid ${theme.chipBorder}`,
                    borderRadius: theme.radius,
                    boxShadow: `0 16px 34px ${theme.cardGlow}`,
                    cursor: 'pointer'
                  }}
                >
                  {/* Entry Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        color: theme.textAccent,
                        marginBottom: '0.5rem'
                      }}>
                        <BookOpen size={20} style={{ display: 'inline', marginRight: '8px' }} />
                        {entry.title}
                      </h3>
                      
                      <div style={{
                        display: 'flex',
                        gap: '1rem',
                        fontSize: '0.75rem',
                        color: '#94a3b8'
                      }}>
                        <span style={{
                          padding: '4px 8px',
                          background: theme.chipBg,
                          border: `1px solid ${theme.chipBorder}`,
                          borderRadius: '999px',
                          textTransform: 'capitalize'
                        }}>
                          {entry.category}
                        </span>
                        <span>Level {entry.spiritualLevel}</span>
                      </div>
                    </div>
                  </div>

                  {/* Mathematical Figure */}
                  <div style={{
                    marginBottom: '1rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '1.5rem',
                    background: theme.panelGradient,
                    borderRadius: theme.radius,
                    border: `1px solid ${theme.chipBorder}`,
                    minHeight: '350px'
                  }}>
                    <WisdomMathFigure
                      wisdomId={entry.id}
                      width={400}
                      height={300}
                      color={theme.accent}
                    />
                  </div>

                  {/* Entry Content */}
                  <p style={{
                    color: '#f1f5f9',
                    lineHeight: '1.6',
                    marginBottom: '1rem'
                  }}>
                    {entry.content}
                  </p>

                  {/* Entry Tags */}
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    flexWrap: 'wrap'
                  }}>
                    {entry.tags.map(tag => (
                      <span
                        key={tag}
                        style={{
                          padding: '4px 8px',
                          background: theme.tagBg,
                          border: `1px solid ${theme.tagBorder}`,
                          borderRadius: '999px',
                          fontSize: '0.75rem',
                          color: theme.textAccent
                        }}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Source and References */}
                  {(entry.source || entry.author || entry.references) && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.75rem',
                      background: theme.tagBg,
                      borderRadius: theme.radius,
                      border: `1px solid ${theme.tagBorder}`,
                      fontSize: '0.75rem'
                    }}>
                      {entry.source && (
                        <div style={{ color: theme.textAccent, marginBottom: '0.25rem' }}>
                          <strong>Source:</strong> {entry.source}
                        </div>
                      )}
                      {entry.author && (
                        <div style={{ color: theme.textAccent, marginBottom: '0.25rem' }}>
                          <strong>Author:</strong> {entry.author}
                        </div>
                      )}
                      {entry.references && entry.references.length > 0 && (
                        <div style={{ color: theme.textAccent, marginTop: '0.5rem' }}>
                          <strong>References:</strong>
                          <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
                            {entry.references.map((ref, idx) => (
                              <li key={idx} style={{ marginBottom: '0.25rem' }}>{ref}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sacred Numerology */}
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    background: theme.chipBg,
                    borderRadius: '999px',
                    border: `1px solid ${theme.chipBorder}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      color: theme.textAccent,
                      fontFamily: 'monospace'
                    }}>
                      Wisdom Level: {entry.spiritualLevel} (R=9)
                    </div>
                    <Star size={16} color={theme.accentSoft} />
                  </div>
                </motion.div>
                );
              })}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                textAlign: 'center',
                padding: '4rem 2rem',
                color: '#64748b'
              }}
            >
              <BookOpen size={60} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
              <h3 style={{ marginBottom: '0.5rem', color: '#8b5cf6' }}>
                No wisdom found
              </h3>
              <p>Try adjusting your search or filters</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Wisdom is free for all to read */}

        {/* Sacred Numerology Display */}
        <motion.div
          className="fr00t-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          style={{
            marginTop: '2rem',
            textAlign: 'center',
            background: 'rgba(99, 102, 241, 0.1)',
            borderColor: 'rgba(99, 102, 241, 0.3)'
          }}
        >
          <h3 style={{ color: '#6366f1', marginBottom: '1rem' }}>
            Sacred Wisdom Statistics
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem'
          }}>
            <div>
              <div style={{ fontSize: '2rem', color: '#6366f1', marginBottom: '0.5rem' }}>
                {wisdomEntries.length}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                Sacred Entries
              </div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', color: '#10b981', marginBottom: '0.5rem' }}>
                {categories.length - 1}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                Sacred Categories
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default WisdomLibrary;
